import EventEmitter from "events";
import NavMapRenderer from "./core/NavMapRenderer";
import GeoLocationProvider from "./core/GeoLocationProvider";
import RoutePlanner from "./core/RoutePlanner";
import InstructionList from "./core/InstructionList";
import { Task } from "@emberly/zenith-client";

export default class NavCore extends EventEmitter {

  constructor(container, t, getAccessTokenSilently, station = null) {
    super();

    // Properties
    this._container = container;
    this._t = t;
    this._getAccessTokenSilently = getAccessTokenSilently;
    this._readyTask = new Task();

    this._rerouteCount = 0;
    this._lastReroute = 0;

    // Services
    this._geolocation = new GeoLocationProvider(this);
    this._renderer = new NavMapRenderer(this, container, station?.location?.coordinates);
    this._routePlanner = new RoutePlanner(this, t);
    this._instructions = new InstructionList(this, t);

    // Wakelock
    this._wakelock = null;

    this._wakelockReleaseFn = () => {
      this._wakelock = null;
    };

    this._visibilityChangeFn = async () => {
      if (this._wakelock === null && document.visibilityState === "visible") {
        this._wakelock = await navigator.wakeLock.request("screen");
        this._wakelock.addEventListener("release", this._wakelockReleaseFn);
      }
    }

    // Setup listeners
    this.setup();
  }

  async setup() {
    if ("wakeLock" in navigator) {
      try {

        this._visibilityChangeFn();
        document.addEventListener("visibilitychange", this._visibilityChangeFn);

      } catch (err) {
        console.log(err);
      }
    }
  }

  setReady() {
    this._readyTask.complete(true);
  }

  async ready() {
    return this._readyTask.wait();
  }

  get geolocation() {
    return this._geolocation;
  }

  async getLocation() {
    return this._geolocation.getLocation();
  }

  get instructions() {
    return this._instructions;
  }

  get renderer() {
    return this._renderer;
  }

  get routePlanner() {
    return this._routePlanner;
  }

  destroy() {
    this.removeAllListeners();
    this._renderer.destroy();
    this._geolocation.destroy();
    this._instructions.destroy();

    document.removeEventListener("visibilitychange", this._visibilityChangeFn);

    if (this._wakelock) {
      try {
        this._wakelock.removeEventListener("release", this._wakelockReleaseFn);
        this._wakelock.release();
      } catch (err) {
        console.log("err", err);
      }
      this._wakelock = null;
    }

  }

  setDestination(loc, vehicleDetails = {}, metadata = null) {
    this._rerouteCount = 0;
    this._routePlanner.setDestination(loc, vehicleDetails, metadata);
    this.renderer.recenter();
  }

  setRoute(route) {
    this._geolocation.setRoute(route);
    this._renderer.setRoute(route);
    this._instructions.setRoute(route);
  }

  cancel() {
    this.emit("cancel");
    this._routePlanner.cancel();
    this._renderer.cancel();
    this._geolocation.cancel();
    this._instructions.cancel();
  }

  pause() {
    // TODO do own pause thing here
    this.cancel();
  }

  reroute() {
    const delta = (Date.now() - this._lastReroute) / 1000.0;
    
    if (delta > 60) {
      this._rerouteCount = Math.max(0, this._rerouteCount - 1);
    } else {
      this._rerouteCount++;
    }

    if (this._rerouteCount < 5) {
      this._routePlanner.reroute();
    } else {
      this.pause();
    }

    this._lastReroute = Date.now();
  }


  reportRoute(routingData) {
    this.emit("route", routingData);
  }


}